/**
 * Promo Code Service
 *
 * Handles promo code application (user-facing) and admin management.
 */

import api from './api';

// ── Types ────────────────────────────────────────────────────────────────────

export interface PromoCodeResult {
  valid: boolean;
  code: string;
  discount_percent: number;
  original_amount: number;
  discounted_amount: number;
  saved_amount: number;
}

export interface PromoCode {
  id: number;
  code: string;
  description: string | null;
  discount_percent: string;
  max_uses: number | null;
  uses_count: number;
  usages_count?: number;
  expires_at: string | null;
  disabled_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface PromoCodeUsage {
  id: number;
  promo_code_id: number;
  user_id: number;
  account_id: number;
  plan_id: number | null;
  original_amount: string;
  discounted_amount: string;
  discount_percent: string;
  status: 'pending' | 'used' | 'expired';
  reserved_at: string;
  used_at: string | null;
  payment_id: number | null;
  created_at: string;
  updated_at: string;
  promo_code?: Pick<PromoCode, 'id' | 'code' | 'discount_percent'>;
  user?: { id: number; name: string; email: string };
  account?: { id: number; name: string };
  plan?: { id: number; name: string } | null;
}

export interface CreatePromoCodeData {
  code: string;
  description?: string;
  discount_percent: number;
  max_uses?: number | null;
  expires_at?: string | null;
}

export interface PaginatedPromoCodesResponse {
  data: PromoCode[];
  current_page: number;
  last_page: number;
  per_page: number;
  total: number;
}

export interface PaginatedUsagesResponse {
  data: PromoCodeUsage[];
  current_page: number;
  last_page: number;
  per_page: number;
  total: number;
}

// ── User-facing ───────────────────────────────────────────────────────────────

/**
 * Apply a promo code for a specific plan and record a pending usage.
 */
export async function applyPromoCode(code: string, planId: number): Promise<PromoCodeResult> {
  const response = await api.post<PromoCodeResult>('/api/promo-codes/apply', {
    code,
    plan_id: planId,
  });
  return response.data;
}

// ── Admin ─────────────────────────────────────────────────────────────────────

export async function adminListPromoCodes(params?: {
  page?: number;
  per_page?: number;
  search?: string;
}): Promise<PaginatedPromoCodesResponse> {
  const response = await api.get<PaginatedPromoCodesResponse>('/api/admin/promo-codes', { params });
  return response.data;
}

export async function adminCreatePromoCode(data: CreatePromoCodeData): Promise<PromoCode> {
  const response = await api.post<{ message: string; promo_code: PromoCode }>(
    '/api/admin/promo-codes',
    data
  );
  return response.data.promo_code;
}

export async function adminUpdatePromoCode(
  id: number,
  data: Partial<CreatePromoCodeData>
): Promise<PromoCode> {
  const response = await api.put<{ message: string; promo_code: PromoCode }>(
    `/api/admin/promo-codes/${id}`,
    data
  );
  return response.data.promo_code;
}

export async function adminDeletePromoCode(id: number): Promise<void> {
  await api.delete(`/api/admin/promo-codes/${id}`);
}

export async function adminTogglePromoCode(id: number): Promise<PromoCode> {
  const response = await api.post<{ message: string; promo_code: PromoCode }>(
    `/api/admin/promo-codes/${id}/toggle`
  );
  return response.data.promo_code;
}

export async function adminListPromoCodeUsages(params?: {
  page?: number;
  per_page?: number;
  status?: 'pending' | 'used' | 'expired' | '';
  search?: string;
}): Promise<PaginatedUsagesResponse> {
  const response = await api.get<PaginatedUsagesResponse>('/api/admin/promo-codes/usages', {
    params,
  });
  return response.data;
}

export const promoCodeService = {
  applyPromoCode,
  adminListPromoCodes,
  adminCreatePromoCode,
  adminUpdatePromoCode,
  adminDeletePromoCode,
  adminTogglePromoCode,
  adminListPromoCodeUsages,
};
